import * as React from "react"
import { FormikConfig, FormikErrors } from "formik"
import { Form, FormElement } from "@modules/form/components/Form"
import { MdArrowForward } from "react-icons/md"
import { CdnVendor, MutationResult } from "@modules/graphql/types"
import { useNetlifySitesListQuery } from "@modules/cdn/netlify/queries.generated"
import { useSetCdnConfigMutation } from "@modules/cdn/shared/queries.generated"
import {
  Button,
  StyledModalActions,
  FormFieldBlock,
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
  Text,
  Notification,
  RenderFieldControl,
  SelectConnectedField,
} from "gatsby-interface"
import {
  netlifyIntegrationConfig as text,
  ui as uiText,
} from "@modules/locales/default.js"
import Loading from "@modules/ui/components/Loading"
import { FormattedMessage } from "@modules/locales"
import { useNetlifyAccountOptions } from "../hooks/useNetlifyAccountOptions"

type FormValues = {
  netlifySite: string
  netlifyAccount: string
  netlifySiteName: string
}

const validateForm: FormikConfig<FormValues>["validate"] = values => {
  const errors: FormikErrors<FormValues> = {}

  if (!values.netlifyAccount) {
    errors.netlifyAccount = text.messages.accountValidateRequired
  }

  if (!values.netlifySite) {
    errors.netlifySite = text.messages.siteValidateRequired
  }

  return errors
}

export type ExistingNetlifyIntegrationFormProps = {
  siteId: string
  onComplete: (
    mutationResult: Pick<MutationResult, "success" | "errorType">
  ) => void
  initialNetlifySiteId: string
  initialSiteName: string
  initialAccount: string
  onSwitchToNew: () => void
  onCancel: () => void
}

export function ExistingNetlifyIntegrationForm({
  siteId,
  onComplete,
  initialNetlifySiteId,
  initialSiteName,
  initialAccount,
  onSwitchToNew,
  onCancel,
}: ExistingNetlifyIntegrationFormProps) {
  const [createIntegration, { loading }] = useSetCdnConfigMutation()
  const [
    netlifyAccountOptions,
    { loading: loadingAccounts },
  ] = useNetlifyAccountOptions(siteId)

  const handleSubmit: FormikConfig<FormValues>["onSubmit"] = values => {
    createIntegration({
      variables: {
        siteId,
        serviceId: values.netlifySite,
        serviceName: values.netlifySiteName,
        accountName: values.netlifyAccount,
        vendor: CdnVendor.Netlify,
      },
    }).then(({ data }) =>
      onComplete({
        success: data?.setCDNConfig?.success ?? false,
        errorType: data?.setCDNConfig?.errorType,
      })
    )
  }

  return (
    <Form<FormValues>
      initialValues={{
        netlifySite: initialNetlifySiteId,
        netlifyAccount: initialAccount,
        netlifySiteName: initialSiteName,
      }}
      validate={validateForm}
      onSubmit={handleSubmit}
    >
      {({
        setFieldValue,
        setValues,
        touched,
        errors,
        values,
        handleSubmit,
      }) => {
        const invalidAccount =
          !values.netlifyAccount ||
          (touched.netlifyAccount && errors.netlifyAccount)

        const invalidSite =
          !values.netlifySite || (touched.netlifySite && errors.netlifySite)

        const disabled = Boolean(invalidAccount || invalidSite)

        return (
          <FormElement applySpacing onSubmit={handleSubmit}>
            <Text css={{ margin: 0 }}>{text.headers.connectToExisting}</Text>

            <Notification
              variant="SECONDARY"
              tone="BRAND"
              content={
                <div>
                  <FormattedMessage<never, "button">
                    message={text.messages.connectToNewSiteCallout}
                    tags={{
                      button: function(content) {
                        return (
                          <Button
                            onClick={onSwitchToNew}
                            variant="GHOST"
                            size="M"
                            css={{ padding: 0, fontSize: `inherit` }}
                            aria-label={text.actions.switchToNewSite}
                          >
                            {content}
                          </Button>
                        )
                      },
                    }}
                  />
                </div>
              }
            />

            <SelectConnectedField
              name="netlifyAccount"
              label={text.labels.selectAccount}
              required
              options={netlifyAccountOptions}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setValues({
                  netlifyAccount: e.target.value,
                  netlifySite: ``,
                  netlifySiteName: ``,
                })
              }}
              disabled={loadingAccounts}
            />

            <NetlifySiteField
              siteId={siteId}
              label={text.labels.selectSite}
              accountValue={values.netlifyAccount}
              selectedNetlifySiteId={values.netlifySite}
              onSelectNetlifySite={item => {
                if (item) {
                  setFieldValue("netlifySite", item.id)
                  setFieldValue("netlifySiteName", item.name)
                }
              }}
              error={touched.netlifySite ? errors.netlifySite : undefined}
            />

            <StyledModalActions>
              <Button variant="SECONDARY" type={`reset`} onClick={onCancel}>
                {uiText.actions.cancel}
              </Button>

              <Button
                type="submit"
                loading={loading}
                disabled={disabled}
                rightIcon={<MdArrowForward />}
              >
                {uiText.actions.save}
              </Button>
            </StyledModalActions>
          </FormElement>
        )
      }}
    </Form>
  )
}

type NetlifySiteFieldProps = {
  siteId: string
  label: React.ReactNode
  accountValue: FormValues["netlifyAccount"]
  selectedNetlifySiteId: string
  onSelectNetlifySite: (site: { id: string; name: string }) => void
  error?: string
}

function NetlifySiteField({
  siteId,
  label,
  accountValue,
  selectedNetlifySiteId,
  onSelectNetlifySite,
  error,
}: NetlifySiteFieldProps) {
  const [initialized, setInitialized] = React.useState<boolean>(
    selectedNetlifySiteId ? false : true
  )
  const [term, setTerm] = React.useState<string>(``)
  const { data, loading } = useNetlifySitesListQuery({
    skip: !accountValue,
    variables: {
      siteId,
      slug: accountValue,
    },
    fetchPolicy: `cache-and-network`,
    onCompleted: data => {
      const selectedSite = (data?.netlifySitesForGatsbySiteId ?? []).find(
        ({ id }) => id === selectedNetlifySiteId
      )
      if (selectedSite) {
        setTerm(selectedSite.name)
        setInitialized(true)
      }
    },
  })

  const netlifySites = data?.netlifySitesForGatsbySiteId ?? []

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = e =>
    setTerm(e.target.value)

  const handleSelect = (value: string) => {
    setTerm(value)
    const selectedSite = netlifySites.find(({ name }) => name === value)
    if (selectedSite) {
      onSelectNetlifySite(selectedSite)
    }
  }

  const options = netlifySites.filter(({ name }) =>
    name.toLowerCase().includes(term.toLowerCase())
  )

  if (!initialized) {
    return (
      <Loading
        delay={1000}
        message={text.messages.loadingSites}
        bufferSize="padded"
        variant="baby"
      />
    )
  }

  return (
    <FormFieldBlock
      id="netlifySiteCombobox"
      label={label}
      error={error}
      required
    >
      {
        (controlProps => (
          <Combobox onSelect={handleSelect}>
            <ComboboxInput
              autoComplete="off"
              disabled={!accountValue}
              placeholder={text.labels.sitePlaceholder}
              showToggleButton
              {...controlProps}
              value={term}
              onChange={handleChange}
            />
            <ComboboxPopover portal>
              {loading ? (
                <Loading
                  delay={1000}
                  message={text.messages.loadingSites}
                  bufferSize="padded"
                  variant="baby"
                />
              ) : options.length > 0 ? (
                <ComboboxList aria-label={text.labels.selectSite}>
                  {options.map(site => {
                    return (
                      <ComboboxOption
                        key={site.id}
                        value={site.name}
                        selected={selectedNetlifySiteId === site.id}
                        highlightMatches={true}
                      />
                    )
                  })}
                </ComboboxList>
              ) : (
                <Text
                  size="S"
                  css={theme => ({
                    marginLeft: theme.space[5],
                    marginRight: theme.space[5],
                    marginTop: theme.space[3],
                    marginBottom: theme.space[3],
                  })}
                >
                  {netlifySites.length > 0
                    ? text.messages.noMatchingSites
                    : text.messages.noSites}
                </Text>
              )}
            </ComboboxPopover>
          </Combobox>
        )) as RenderFieldControl
      }
    </FormFieldBlock>
  )
}
